/**
 * CampaignsPage - Campanhas e Anúncios Ads (simulated)
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Megaphone, Plus, Edit3, Trash2, Loader2, Target, Users, DollarSign } from "lucide-react";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { CHANNELS } from "@/lib/channels";
import StatusBadge from "@/components/shared/StatusBadge";
import ChannelIcon from "@/components/shared/ChannelIcon";
import EmptyState from "@/components/shared/EmptyState";

const OBJECTIVES = [
    { v: "awareness", label: "Notoriedade" },
    { v: "traffic", label: "Tráfego" },
    { v: "engagement", label: "Interação" },
    { v: "leads", label: "Leads" },
    { v: "sales", label: "Vendas" },
];

export default function CampaignsPage() {
    const [campaigns, setCampaigns] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: "",
        objective: "awareness",
        budget_aoa: 50000,
        budget_usd: 60,
        duration_days: 7,
        channels: ["facebook", "instagram"],
        creative_caption: "",
        audience_location: "Luanda, Angola",
        audience_age: "25-55",
        status: "ativa",
    });

    const load = () => api.get("/campaigns").then((r) => setCampaigns(r.data));
    useEffect(() => { load(); }, []);

    const openNew = () => {
        setEditing(null);
        setForm({
            name: "",
            objective: "awareness",
            budget_aoa: 50000,
            budget_usd: 60,
            duration_days: 7,
            channels: ["facebook", "instagram"],
            creative_caption: "",
            audience_location: "Luanda, Angola",
            audience_age: "25-55",
            status: "ativa",
        });
        setOpen(true);
    };
    const openEdit = (c) => {
        setEditing(c);
        setForm({
            name: c.name,
            objective: c.objective,
            budget_aoa: c.budget_aoa,
            budget_usd: c.budget_usd,
            duration_days: c.duration_days,
            channels: c.channels || [],
            creative_caption: c.creative_caption || "",
            audience_location: c.audience?.location || "",
            audience_age: c.audience?.age_range || "",
            status: c.status,
        });
        setOpen(true);
    };
    const remove = async (c) => {
        if (!window.confirm("Eliminar campanha?")) return;
        await api.delete(`/campaigns/${c.id}`);
        toast.success("Campanha eliminada");
        load();
    };
    const save = async () => {
        setSaving(true);
        try {
            const body = {
                name: form.name,
                objective: form.objective,
                audience: { location: form.audience_location, age_range: form.audience_age },
                budget_aoa: Number(form.budget_aoa),
                budget_usd: Number(form.budget_usd),
                duration_days: Number(form.duration_days),
                channels: form.channels,
                creative_caption: form.creative_caption,
                status: form.status,
            };
            if (editing) {
                await api.put(`/campaigns/${editing.id}`, body);
                toast.success("Campanha atualizada");
            } else {
                await api.post("/campaigns", body);
                toast.success("Campanha criada");
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(getApiErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const toggleChannel = (t) =>
        setForm((f) => ({
            ...f,
            channels: f.channels.includes(t) ? f.channels.filter((x) => x !== t) : [...f.channels, t],
        }));

    return (
        <div className="space-y-6" data-testid="campaigns-page">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                        Campanhas e Anúncios Ads
                    </h1>
                    <p className="text-muted-foreground">Crie e gerencie as suas campanhas pagas.</p>
                </div>
                <Button onClick={openNew} className="btn-grad-purple text-white" data-testid="ads-new-campaign-button">
                    <Plus className="h-4 w-4 mr-2" /> Nova campanha
                </Button>
            </div>

            {campaigns.length === 0 ? (
                <EmptyState
                    icon={Megaphone}
                    title="Sem campanhas ainda"
                    description="Crie a sua primeira campanha Ads. Definimos orçamento, audiência e criativos por si."
                    ctaLabel="Criar campanha"
                    onCta={openNew}
                    testid="campaigns-empty"
                />
            ) : (
                <div className="grid lg:grid-cols-2 gap-4">
                    {campaigns.map((c) => (
                        <Card key={c.id} className="glass-card p-5 rounded-2xl border-white/10" data-testid={`campaign-${c.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <h3 className="font-display font-semibold text-lg">{c.name}</h3>
                                    <div className="flex items-center gap-2 mt-1">
                                        <StatusBadge status={c.status} />
                                        <Badge variant="outline" className="text-xs">{OBJECTIVES.find(o => o.v === c.objective)?.label}</Badge>
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`campaign-edit-${c.id}`}>
                                        <Edit3 className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => remove(c)} data-testid={`campaign-delete-${c.id}`}>
                                        <Trash2 className="h-3.5 w-3.5 text-[#FB7185]" />
                                    </Button>
                                </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {c.channels?.map((ch) => <ChannelIcon key={ch} type={ch} showBg className="h-3 w-3" />)}
                            </div>
                            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <Stat label="Orçamento" value={`${c.budget_aoa.toLocaleString("pt-PT")} AOA`} />
                                <Stat label="Duração" value={`${c.duration_days} dias`} />
                                <Stat label="Alcance" value={(c.metrics?.reach || 0).toLocaleString("pt-PT")} />
                                <Stat label="CTR" value={`${c.metrics?.ctr || 0}%`} />
                            </div>
                            {c.metrics?.impressions > 0 && (
                                <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                                    <span><span className="text-muted-foreground">Impressões:</span> {c.metrics.impressions.toLocaleString("pt-PT")}</span>
                                    <span><span className="text-muted-foreground">Cliques:</span> {c.metrics.clicks}</span>
                                    <span><span className="text-muted-foreground">Conversões:</span> {c.metrics.conversions}</span>
                                </div>
                            )}
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Editar campanha" : "Nova campanha"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Nome da campanha</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-name-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Objetivo</Label>
                                <Select value={form.objective} onValueChange={(v) => setForm({ ...form, objective: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {OBJECTIVES.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Estado</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="rascunho">Rascunho</SelectItem>
                                        <SelectItem value="ativa">Ativa</SelectItem>
                                        <SelectItem value="pausada">Pausada</SelectItem>
                                        <SelectItem value="concluida">Concluída</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label>Orçamento (AOA)</Label>
                                <Input type="number" value={form.budget_aoa} onChange={(e) => setForm({ ...form, budget_aoa: e.target.value })} data-testid="ads-budget-input" />
                            </div>
                            <div>
                                <Label>Orçamento (USD)</Label>
                                <Input type="number" value={form.budget_usd} onChange={(e) => setForm({ ...form, budget_usd: e.target.value })} />
                            </div>
                            <div>
                                <Label>Duração (dias)</Label>
                                <Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Localização (audiência)</Label>
                                <Input value={form.audience_location} onChange={(e) => setForm({ ...form, audience_location: e.target.value })} data-testid="ads-audience-location" />
                            </div>
                            <div>
                                <Label>Idade</Label>
                                <Input value={form.audience_age} onChange={(e) => setForm({ ...form, audience_age: e.target.value })} placeholder="25-55" />
                            </div>
                        </div>
                        <div>
                            <Label>Canais</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {CHANNELS.map((c) => {
                                    const Icon = c.icon;
                                    const active = form.channels.includes(c.type);
                                    return (
                                        <button
                                            key={c.type}
                                            type="button"
                                            onClick={() => toggleChannel(c.type)}
                                            className={`px-3 py-1.5 rounded-lg border text-xs font-medium flex items-center gap-1.5 ${
                                                active
                                                    ? "border-[#6B21D9] bg-[#6B21D9]/15 text-[#A78BFA]"
                                                    : "border-white/10 text-muted-foreground hover:bg-white/5"
                                            }`}
                                        >
                                            <Icon className="h-3.5 w-3.5" /> {c.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <Label>Texto criativo</Label>
                            <Textarea rows={3} value={form.creative_caption} onChange={(e) => setForm({ ...form, creative_caption: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} disabled={saving} className="btn-grad-purple text-white" data-testid="ads-save-campaign-button">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            {editing ? "Atualizar" : "Criar campanha"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="text-[10px] text-muted-foreground uppercase">{label}</div>
            <div className="font-display text-sm font-semibold">{value}</div>
        </div>
    );
}
